/**
 * COLLECTION DETAIL MODALS
 *
 * Renders the appropriate detail modal based on the selected item's type.
 * Extracted from Collection.tsx to reduce JSX tree complexity.
 */

import type { CollectionItem } from '../../types';
import type { BrandCategory } from '../../types/brandCategories';
import PolishDetailModal from '../polish/PolishDetailModal';
import PlateDetailModal from '../plates/PlateDetailModal';
import DecalDetailModal from '../decals/DecalDetailModal';
import EmbellishmentDetailModal from '../embellishments/EmbellishmentDetailModal';
import ToolDetailModal from '../tools/ToolDetailModal';
import BuffingPowderDetailModal from '../powders/BuffingPowderDetailModal';
import AcrylicDetailModal from '../acrylics/AcrylicDetailModal';
import CuticleCareDetailModal from '../cuticleCare/CuticleCareDetailModal';

interface CollectionDetailModalsProps {
  selectedItem: CollectionItem | null;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<CollectionItem>) => Promise<void>;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
  onPermanentDelete: (id: string) => void;
  isInTrash: boolean;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  // Brand autocomplete props
  existingBrands?: string[];
  userBrandCategories?: Record<string, BrandCategory>;
}

export default function CollectionDetailModals({
  selectedItem,
  onClose,
  onUpdate,
  onDelete,
  onRestore,
  onPermanentDelete,
  isInTrash,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  existingBrands = [],
  userBrandCategories = {},
}: CollectionDetailModalsProps) {
  if (!selectedItem) return null;

  const commonProps = {
    onClose,
    onUpdate: async (_id: string, updates: Partial<CollectionItem>) => {
      await onUpdate(selectedItem.id, updates);
    },
    onDelete,
    onRestore,
    onPermanentDelete,
    isInTrash,
    onPrev,
    onNext,
    hasPrev,
    hasNext,
  };

  switch (selectedItem.itemType) {
    case 'polish':
      return (
        <PolishDetailModal
          key={selectedItem.id}
          polish={selectedItem}
          {...commonProps}
          existingBrands={existingBrands}
          userBrandCategories={userBrandCategories}
        />
      );
    case 'plate':
      return <PlateDetailModal key={selectedItem.id} plate={selectedItem} {...commonProps} />;
    case 'transfer':
      return <DecalDetailModal key={selectedItem.id} decal={selectedItem} {...commonProps} />;
    case 'embellishment':
      return <EmbellishmentDetailModal key={selectedItem.id} embellishment={selectedItem} {...commonProps} />;
    case 'tool':
      return <ToolDetailModal key={selectedItem.id} tool={selectedItem} {...commonProps} />;
    case 'powder':
      return <BuffingPowderDetailModal key={selectedItem.id} powder={selectedItem} {...commonProps} />;
    case 'acrylic':
      return <AcrylicDetailModal key={selectedItem.id} acrylic={selectedItem} {...commonProps} />;
    case 'cuticle_care':
      return <CuticleCareDetailModal key={selectedItem.id} cuticleItem={selectedItem} {...commonProps} />;
    default:
      return null;
  }
}
